// Check pages copy (spec §9.3; 05 §6; 06 §7.4 for bulk). Every user-facing
// string of the shared workspace lives here so the bulk host can override the
// few that differ through the `labels` prop.

const plural = (n, one, many) => (n === 1 ? one : many);

export const SUGGESTION_REASONS = Object.freeze({
  ID_MATCH: () => 'Student ID on the page matches',
  NAME_MATCH: () => 'Name on the page matches',
  TEXT_CONTINUES: (s) =>
    s?.referencePage
      ? `Continues the last sentence on page ${s.referencePage}`
      : 'Continues the last sentence on the previous page',
  PAGE_NUMBER: () => 'Page number follows',
  QUESTION_SEQUENCE: () => 'Question numbers follow',
  HANDWRITING: () => 'Similar handwriting',
});

/** "94% · Student ID on the page matches". */
export const suggestionText = (suggestion) => {
  if (!suggestion) return '';
  const pct = Math.round((suggestion.confidence ?? 0) * 100);
  const reason = SUGGESTION_REASONS[suggestion.reason]?.(suggestion);
  return reason ? `${pct}% · ${reason}` : `${pct}%`;
};

export const MATCH_NOTES = Object.freeze({
  MATCHED_FROM_COVER: '✓ Matched from the cover page',
  ASSIGNED_BY_YOU: '✓ Assigned by you',
  CONFIRMED_BY_YOU: '✓ Confirmed by you',
  ADDED_BY_YOU: '✓ Added by you',
});

const REJECTIONS = Object.freeze({
  PAGE_NOT_FOUND: "That page isn't in this upload any more.",
  SCRIPT_NOT_FOUND: "That script isn't there any more.",
  PAGE_SET_ASIDE: 'That page is set aside. Restore it first.',
  POSITION_OUT_OF_RANGE: "That position isn't in the script.",
  NOT_A_PERMUTATION: 'The page order changed. Try again.',
  SCRIPT_ID_TAKEN: 'That script already exists.',
  EMPTY_SCRIPT: 'Choose at least one page.',
  TOO_MANY_SCRIPTS: 'Up to 3,000 pages and 300 scripts per exam.',
  CANNOT_SPLIT_FIRST_PAGE: 'This is already the first page of the script.',
  NAME_OR_ID_REQUIRED: 'Type a name or an ID.',
  ROSTER_ENTRY_NOT_FOUND: "That student isn't on the list any more.",
  // 01 §6 / 06 D11: a class task's Bulk Upload commits only class members.
  NOT_A_CLASS_MEMBER: 'Pick a student from this class.',
  NOTHING_TO_CONFIRM: 'This match is already confirmed.',
  ALREADY_THAT_KIND: 'That page has already changed.',
  PAGE_NOT_IN_SCRIPT: "That page isn't in this script.",
  LINE_OUT_OF_RANGE: "That line isn't on the page.",
  QUESTION_NOT_FOUND: "That question isn't in this exam.",
  CONFIDENCE_TOO_LOW:
    'Only suggestions JeddAI is at least 90% sure of can be accepted.',
  PAGE_TYPED_USE_TYPED_EDIT:
    'Typed responses are edited by removing them and pasting them again.',
  TEXT_TOO_LONG: 'A page can hold up to 20,000 characters.',
  NOT_EDITED: "This page's transcription hasn't been edited.",
  INVALID_ROTATION: 'Pages turn in quarter turns.',
  ALREADY_THAT_MODE: 'The pages are already sorted that way.',
  INVALID_MODE: "That sorting mode isn't available.",
  TOO_MANY_COMMANDS: 'That change is too big to make in one go.',
});

export const rejectionMessage = (reason, fallback) =>
  REJECTIONS[reason] || fallback || "That change isn't possible.";

export const CHECK_PAGES_COPY = Object.freeze({
  title: 'Check pages',
  scripts: (n) => `${n} ${plural(n, 'script', 'scripts')}`,
  toFixTab: (n) => `To fix (${n})`,
  allTab: (n, labels) => `${labels?.allTab || 'All scripts'} (${n})`,
  search: 'Search name, ID or script',
  groupBySuggested: 'Group by suggested script',
  setAsideTray: (n) => `Set aside (${n})`,
  restore: 'Restore',
  nothingToFix: 'Nothing left to fix.',
  uploadRoster: 'Upload a name/ID list',
  undo: 'Undo',
  undoTitle: 'Undo last change',
  handwritten: 'Handwritten',
  typed: 'Typed',
  railToggleShow: 'Show the list',
  railToggleHide: 'Hide the list',
  previous: 'Previous',
  next: 'Next',
  loosePage: 'Loose page',
  loosePageN: (n) => `Loose page ${n}`,
  looseSub: 'Not in a script yet',
  belongsTo: 'Belongs to',
  addToEnd: 'Add to end',
  backToSuggested: 'Back to suggested match',
  newScript: 'New script',
  insertBefore: 'Insert before page 1',
  insertBetween: (i) => `Insert between pages ${i} and ${i + 1}`,
  insertAfter: (n) => `Insert after page ${n}`,
  notAResponse: 'Not part of a response',
  setAsideNotMarked: 'set aside, not marked',
  restoreAsResponse: 'Restore as part of the response',
  scriptBy: 'Script by',
  namePlaceholder: 'Type the name or ID on the script',
  idPlaceholder: 'ID (optional)',
  assignNext: 'Assign & next',
  // Nothing typed yet (tplA 2453 saveLabel).
  addNext: 'Add & next',
  addNewStudent: 'Add as new student',
  isThis: (name) => `Is this ${name}?`,
  yesNext: 'Yes, next',
  or: 'or',
  chooseStudent: 'Choose a student…',
  sure: (pct) => `${pct}% sure`,
  useId: (id) => `Use ID ${id}`,
  alreadyHandedIn: 'Already handed in',
  // 06 C2: a reassigned student; the upload becomes their resubmission.
  resubmissionRequested: 'Resubmission requested',
  pickFromClass: 'Pick a student from this class.',
  pageOf: (i, n) => `Page ${i} of ${n}`,
  added: 'added',
  movedFrom: (name) => `moved from ${name}`,
  starts: (labels) =>
    `${labels.join(', ')} start${labels.length === 1 ? 's' : ''}`,
  pageMenu: 'Page actions',
  moveToScript: 'Move to another script',
  movePageN: (n) => `Move page ${n} to a different script`,
  moveTo: (label) => `Move ${label} to`,
  chooseScript: 'Choose a script…',
  move: 'Move',
  cancel: 'Cancel',
  splitHere: 'Start a new script from this page',
  editTranscription: 'Edit transcription',
  setStart: (label) => `Set where ${label} starts`,
  rotate: 'Rotate',
  retryRead: 'Retry read',
  readFailed: "We couldn't read this page.",
  reading: 'Reading…',
  noText: 'No text yet',
  dragToReorder: 'Drag to reorder',
  moveToPosition: 'Move to position…',
  position: (n) => `Position ${n}`,
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  questionStartMissing: (label) => `Couldn't find where ${label} starts`,
  setIt: 'Set it',
  pickStartTitle: (label) => `Where does ${label} start?`,
  pickStartBody: 'Click the line where the answer begins.',
  clearStart: 'Clear',
  bulkAccept: (n) => `Accept ${n} ${plural(n, 'suggestion', 'suggestions')}`,
  bulkAcceptTail: 'JeddAI is at least 90% sure of',
  everyPageMatched: 'Every page is matched',
  reviewAll: 'Review all scripts',
  edit: 'Edit',
  transcriptionEdited: 'Transcription edited',
  editConfirmTitle: 'Edit the transcription?',
  editConfirmBody:
    'Editing the transcription will change the response that JeddAI marks. The script will be tagged as edited and kept on record.',
  continue: 'Continue',
  save: 'Save',
  saveTranscription: 'Save transcription',
  useJeddaiText: "Use JeddAI's text",
  edited: 'Edited',
  typedPageNote:
    'Typed responses are edited by removing them and pasting them again.',
  rosterTitle: 'Upload a name/ID list',
  rosterBody:
    'Add the names and IDs of the students who sat the exam. JeddAI uses them to match scripts.',
  rosterFile: 'Choose a CSV or Excel file',
  rosterPaste: 'Or paste names and IDs, one student per line (Name, ID)',
  rosterAdd: 'Add names',
  rosterAdded: (added, rematched) =>
    `Added ${added} ${plural(added, 'name', 'names')}${
      rematched
        ? ` · ${rematched} ${plural(rematched, 'script', 'scripts')} matched`
        : ''
    }`,
  rosterEmpty: 'Add a file or paste at least one name.',
  changedElsewhere:
    'Some pages changed elsewhere. We reloaded the latest version.',
  notSaved: 'Changes not saved yet',
  retry: 'Retry',
  refused: "That change couldn't be saved. We reloaded the latest version.",
  tooMany: 'That change is too big to make in one go.',
  undone: (label) => (label ? `Undone: ${label}` : 'Undone'),
  noName: 'No name or ID',
  noNameFound: 'No name or ID found',
  needsNameOrId: 'Needs a name or ID',
  // Toolbar sub for a script without a name (tplA 2448): "2 pages · no name
  // or ID found".
  pagesNoName: (n, isNew) =>
    `${n} ${plural(n, 'page', 'pages')} · ${
      isNew ? 'needs a name or ID' : 'no name or ID found'
    }`,
  loosePageOf: (i, n) => `Loose page ${i} of ${n}`,
  tagUnassigned: 'Unassigned',
  tagNewScript: 'New script',
  tagMatched: 'Matched',
  tagDuplicate: 'Duplicate',
  checkMatch: 'Check match',
  newScriptSub: 'New script',
  pages: (n) => `${n} ${plural(n, 'page', 'pages')}`,
  duplicate: (name) => `Two responses are assigned to ${name}`,
  // Bulk (06 §7.4)
  sortManually: 'Sort manually',
  sortForMe: 'Sort pages for me',
  sortAgainTitle: 'Sort pages again?',
  sortAgainBody:
    'JeddAI will re-sort every page. Your changes so far can be undone.',
  sortAgainConfirm: 'Sort again',
  loosePagesGroup: (n) => `Loose pages (${n})`,
  select: 'Select',
  deselect: 'Deselect',
  assignPages: (n) => `Assign ${n} ${plural(n, 'page', 'pages')} to`,
  assign: 'Assign',
  autoSortFailed:
    "We couldn't sort these pages automatically. Sort them by hand, or try again.",
});

// Labels the host may override (05 §6.1 `labels`; 06 §7.4 bulk copy).
export const DEFAULT_LABELS = Object.freeze({
  script: 'script',
  scriptBy: 'Script by',
  allTab: 'All scripts',
  arrivalFound: (n) => `${n} ${plural(n, 'script', 'scripts')} found`,
  arrivalAttention: (k) =>
    `${k} ${plural(k, 'item needs', 'items need')} your attention`,
  arrivalDetail: 'Loose pages and scripts without a name or ID',
  arrivalCheck: 'Check unassigned pages',
  arrivalAllMatched: (n) =>
    `All ${n} ${plural(n, 'script is', 'scripts are')} matched to a student`,
  arrivalReview: 'Review scripts',
  stillToFixTitle: (n) => `${n} ${plural(n, 'item', 'items')} still to fix`,
  stillToFixBody:
    "Loose pages won't be marked. Unassigned scripts are marked, but won't have a name in your results.",
  questionStartLine: (label, k) =>
    `Couldn't find where ${label} starts on ${k} ${plural(
      k,
      'script',
      'scripts'
    )}. Those answers won't be marked by JeddAI.`,
  continueAnyway: 'Continue anyway',
  goBackAndFix: 'Go back and fix',
});

export const BULK_LABELS = Object.freeze({
  script: 'student',
  scriptBy: 'Student',
  allTab: 'All students',
  arrivalFound: (n) => `${n} ${plural(n, 'student', 'students')} found`,
  arrivalAllMatched: (n) =>
    `All ${n} ${plural(
      n,
      'response is',
      'responses are'
    )} matched to a student`,
  arrivalReview: 'Review responses',
  stillToFixBody:
    "Loose pages and responses without a student won't be uploaded or marked.",
  // Class task (06 D11, §7.4): every response the server counts as checkMatch
  // is left out: a roster match still to check (0.5–0.8, a student name and
  // the Check match chip), a read name that matched nobody (New script) and a
  // name that is no class member.
  checkMatchLine: (n) =>
    `${n} ${plural(
      n,
      "response isn't",
      "responses aren't"
    )} matched to a class student yet (Check match or New script). ${plural(
      n,
      "It won't",
      "They won't"
    )} be uploaded until you confirm the student.`,
});

export const resolveLabels = (labels) => ({
  ...DEFAULT_LABELS,
  ...(labels || {}),
});
